import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import AsyncSelect from 'react-select/async';
import Sidebar from "../../../components/sidebar/Sidebar";
import { useMask } from "../../../utils/privacyMask";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

export default function AddPointsPage() {
  const { mask } = useMask();
  const [searchParams] = useSearchParams();

  const [accounts, setAccounts]     = useState([]);
  const [selected, setSelected]     = useState(null);   // { value: _id, label, account }
  const [current, setCurrent]       = useState(null);
  const [form, setForm]             = useState({ amount: '', action: 'add' });
  const [success, setSuccess]       = useState(null);
  const [error, setError]           = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPts, setLoadingPts] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const API = process.env.REACT_APP_API_URL;

  /* ── Load accounts ── */
  useEffect(() => {
    axios.get(`${API}/account`, { headers })
      .then(r => {
        setAccounts(r.data);
        // Pre-select if accountId passed via query param
        const preId = searchParams.get('accountId');
        if (preId) {
          const match = r.data.find(a => a._id === preId);
          if (match) {
            const opt = accountToOption(match);
            setSelected(opt);
            fetchPoints(match);
          }
        }
      })
      .catch(() => {});
  }, []);

  const accountToOption = (a) => ({
    value: a._id,
    label: `${a.username}${a.discord ? ` · ${a.discord}` : ''}${a.email ? ` (${a.email})` : ''}`,
    account: a,
  });

  const fetchPoints = async (account) => {
    setLoadingPts(true);
    setCurrent(null);
    try {
      // Try by accountID first, then fall back to discordID
      let pts = null;
      try {
        const r = await axios.get(`${API}/referralpoints/account/${account._id}`, { headers });
        pts = r.data;
      } catch {
        if (account.discordID) {
          const r = await axios.get(`${API}/referralpoints/discord/${account.discordID}`, { headers }).catch(() => null);
          if (r?.data) pts = r.data;
        }
      }
      setCurrent(pts);
    } finally {
      setLoadingPts(false);
    }
  };

  const loadOptions = (inputValue, callback) => {
    const q = inputValue.toLowerCase();
    const filtered = accounts.filter(a =>
      a.username?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.discord?.toLowerCase().includes(q)
    );
    callback(filtered.map(accountToOption));
  };

  const handleAccountSelect = (opt) => {
    setSelected(opt);
    setSuccess(null);
    setError(null);
    if (opt) fetchPoints(opt.account);
    else setCurrent(null);
  };

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const amount = Number(form.amount);
    if (!selected)          return setError('Please select an account.');
    if (!amount || amount <= 0) return setError('Enter a positive amount.');

    setSubmitting(true);
    const account = selected.account;

    try {
      const endpoint = form.action === 'add' ? '/referralpoints/add' : '/referralpoints/remove';
      const body = {
        accountID: account._id,
        discordId: account.discordID || undefined,
        discordUsername: account.discord || account.username,
        amount,
      };

      const res = await axios.post(`${API}${endpoint}`, body, { headers });
      setCurrent(res.data.data);
      setSuccess(
        form.action === 'add'
          ? `Added ${amount} points to ${mask(account.username, 'text')}. New balance: ${mask(res.data.data.availablePoints, 'number')}`
          : `Removed ${amount} points from ${mask(account.username, 'text')}. New balance: ${mask(res.data.data.availablePoints, 'number')}`
      );
      setForm(prev => ({ ...prev, amount: '' }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update points');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Sidebar title="Add / Remove Points" />

      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div className="form-panel-icon">
              <i className="bi bi-star-fill" />
            </div>
            <div>
              <div className="form-panel-title">Adjust Points</div>
              <div className="form-panel-subtitle">Add or remove points for an account</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* ── Account picker ── */}
              <div className="form-section-label">Account</div>
              <div className="form-field">
                <label className="form-field-label">
                  <i className="bi bi-person-fill" />
                  Select Account
                  <span className="required-star">*</span>
                </label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadOptions}
                  onChange={handleAccountSelect}
                  value={selected}
                  isClearable
                  placeholder="Search by username, email or Discord…"
                  styles={getSelectStyles()}
                />
              </div>

              {/* ── Current balance ── */}
              {loadingPts && (
                <div style={{ marginBottom: 20, padding: '10px 14px', background: 'var(--color-primary-bg)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-muted)', fontSize: '0.82rem' }}>
                  <i className="bi bi-hourglass-split me-2" />Looking up points…
                </div>
              )}

              {!loadingPts && selected && (
                <div style={{ marginBottom: 20, padding: '14px 18px', background: 'var(--color-primary-bg)', border: '1px solid var(--color-border)', borderRadius: 10, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                  {current ? (
                    <>
                      <div>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-muted)', marginBottom: 3 }}>Total Earned</div>
                        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-primary-l)' }}>{mask(current.totalPointsEarned?.toLocaleString(), 'number')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-muted)', marginBottom: 3 }}>Available</div>
                        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-success)' }}>{mask(current.availablePoints?.toLocaleString(), 'number')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-muted)', marginBottom: 3 }}>Spent</div>
                        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-danger)' }}>
                          {mask(((current.totalPointsEarned ?? 0) - (current.availablePoints ?? 0)).toLocaleString(), 'number')}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                      <i className="bi bi-info-circle me-2" />No points record yet — one will be created on first addition.
                    </div>
                  )}
                </div>
              )}

              {/* ── Adjustment ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Adjustment</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-toggles" />
                      Action
                      <span className="required-star">*</span>
                    </label>
                    <select className="form-input" name="action" value={form.action} onChange={handleChange}>
                      <option value="add">Add Points</option>
                      <option value="remove">Remove Points</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-123" />
                      Amount
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      name="amount"
                      value={form.amount}
                      onChange={handleChange}
                      placeholder="e.g. 100"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {success && (
                <div className="form-banner success">
                  <i className="bi bi-check-circle-fill" />{success}
                </div>
              )}
              {error && (
                <div className="form-banner error">
                  <i className="bi bi-exclamation-triangle-fill" />{error}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button
                  type="submit"
                  className="btn-form-submit"
                  disabled={submitting}
                  style={form.action === 'remove' ? { background: 'rgba(139,58,58,0.18)', borderColor: 'rgba(139,58,58,0.4)', color: '#c47070' } : {}}
                >
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : form.action === 'add' ? 'bi-plus-lg' : 'bi-dash-lg'}`} />
                  {submitting ? 'Saving…' : form.action === 'add' ? 'Add Points' : 'Remove Points'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
